import {HoistService, InitContext, PlainObject, XH} from '@xh/hoist/core';
import {MINUTES} from '@xh/hoist/utils/datetime';
import {getDefaultOptions} from '../../core/ServiceUtils';
import {ResearchDataset, ResearchDatasetRows, ResearchDataSource, ResearchEnsureResult} from '../data/ResearchTypes';

/**
 * Access to the backend's research datasets (`researchData/*`): the datasets a dashboard widget can
 * show, as described by the data catalog, and their rows for one ticker. Rows are cached per ticker
 * and source, so widgets showing the same data share one request.
 */
export class ResearchService extends HoistService {
    datasets: ResearchDataset[] = [];

    private rowsCache = new Map<string, Promise<PlainObject[]>>();

    override async initAsync(ctx: InitContext) {
        try {
            this.datasets = await XH.fetchJson({
                ...getDefaultOptions(),
                url: `${this.baseUrl}/datasets`
            });
        } catch (e) {
            // The Research tab shows that no data is available - the rest of the app is unaffected
            XH.handleException(e, {showAlert: false, title: 'Error loading research datasets'});
        }
    }

    getDataset(name: string): ResearchDataset {
        return this.datasets.find(d => d.name === name) ?? null;
    }

    /**
     * Make sure the backend holds research data for the ticker. For a ticker it has nothing for yet it
     * fetches every dataset before answering, which takes a while.
     */
    async ensureTickerAsync(ticker: string): Promise<ResearchEnsureResult> {
        return XH.postJson({
            ...getDefaultOptions(),
            timeout: 3 * MINUTES,
            url: `${this.baseUrl}/ensure/${encodeURIComponent(ticker)}`
        });
    }

    /** Rows of the source's dataset for the ticker, oldest first. */
    async queryAsync(source: ResearchDataSource, ticker: string): Promise<PlainObject[]> {
        const {dataset, latest = false, filters = []} = source,
            body = {dataset, ticker, latest, filters},
            key = JSON.stringify(body);

        let ret = this.rowsCache.get(key);
        if (!ret) {
            ret = this.fetchRowsAsync(body);
            this.rowsCache.set(key, ret);
            ret.catch(() => this.rowsCache.delete(key));
        }
        return ret;
    }

    clearCache() {
        this.rowsCache.clear();
    }

    //------------------------
    // Implementation
    //------------------------
    private get baseUrl(): string {
        return `${XH.getConf('svcApiUrl')}/researchData`;
    }

    private async fetchRowsAsync(body: PlainObject): Promise<PlainObject[]> {
        const result: ResearchDatasetRows = await XH.postJson({
            ...getDefaultOptions(),
            url: `${this.baseUrl}/query`,
            body
        });
        return result.rows;
    }
}
